import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Tag, Button, Space, message, Spin, Image, Table } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import { SectionType, SectionTypeStatus, SectionDetail } from '../../types/sectionType';
import sectionTypeService from '../../services/sectionTypeService';
import { formatTimestamp } from '../../utils/responseTransformer';
import './sectionType.css';

interface SectionTypeDetailProps {
  // 可以添加额外的props
}

const SectionTypeDetail: React.FC<SectionTypeDetailProps> = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [sectionType, setSectionType] = useState<SectionType | null>(null);
  const [sectionDetails, setSectionDetails] = useState<SectionDetail[]>([]);

  // 获取货架类型详情
  const fetchSectionTypeDetail = async () => {
    if (!id) {
      message.error(t('sectionType.idRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await sectionTypeService.getSectionTypeById(Number(id));
      if (response.success && response.data) {
        setSectionType(response.data);
        // 填充货架明细
        if (response.data.sectionDetails && response.data.sectionDetails.length > 0) {
          setSectionDetails(response.data.sectionDetails);
        }
      } else {
        message.error(response.errorMessage || t('sectionType.getDetailFailed'));
      }
    } catch (error) {
      console.error('获取货架类型详情失败:', error);
      message.error(t('sectionType.getDetailFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectionTypeDetail();
  }, [id]);

  // 返回列表
  const handleBack = () => {
    navigate('/section-type');
  };

  // 编辑货架类型
  const handleEdit = () => {
    if (!sectionType) return;
    
    if (sectionType.status !== 'DRAFT') {
      message.warning(t('sectionType.onlyDraftCanEdit'));
      return;
    }
    
    navigate(`/section-type/edit/${sectionType.id}`);
  };

  // 渲染状态标签
  const renderStatusTag = (status: SectionTypeStatus) => {
    const colorMap = {
      DRAFT: 'default',
      ENABLE: 'success',
      DISABLE: 'error',
    };
    const labelMap = {
      DRAFT: t('sectionType.statusTest.DRAFT'),
      ENABLE: t('sectionType.statusTest.ENABLE'),
      DISABLE: t('sectionType.statusTest.DISABLE'),
    };
    return (
      <Tag color={colorMap[status]}>
        {labelMap[status]}
      </Tag>
    );
  };

  if (loading) {
    return (
      <div className="section-type-detail">
        <div className="section-type-loading">
          <Spin size="large" tip={t('common.loading')} />
        </div>
      </div>
    );
  }

  if (!sectionType) {
    return (
      <div>
        <PageHeader
          title={t('sectionType.detailTitle')}
          backPath="/section-type"
        />
        <Card>
          <div className="section-type-empty">
            <p>{t('sectionType.notFound')}</p>
            <Button type="primary" onClick={handleBack}>
              {t('common.back')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('sectionType.detailTitle')}
        subtitle={sectionType.typeName}
        backPath="/section-type"
        extra={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              {t('common.back')}
            </Button>
            {sectionType.status === 'DRAFT' && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                {t('common.edit')}
              </Button>
            )}
          </Space>
        }
      />

      <Card title={t('sectionType.basicInfo')} className="section-type-info-card">
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('sectionType.typeCode')}>
            {sectionType.typeCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.typeName')}>
            {sectionType.typeName}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.status')}>
            {renderStatusTag(sectionType.status)}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.description')} span={1}>
            {sectionType.description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.innerLength')} span={1}>
            {sectionType.innerLength || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.innerWidth')} span={1}>
            {sectionType.innerWidth || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.innerHeight')} span={1}>
            {sectionType.innerHeight || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.outerLength')} span={1}>
            {sectionType.outerLength || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.outerWidth')} span={1}>
            {sectionType.outerWidth || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.outerHeight')} span={1}>
            {sectionType.outerHeight || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('sectionType.operationInfo')} className="section-type-operation-card">
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('common.creator')}>
            {sectionType.creatorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.creatorAccount')}>
            {sectionType.creatorAccount}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.createTime')}>
            {formatTimestamp(sectionType.gmtCreate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.modifier')}>
            {sectionType.modifierName}
          </Descriptions.Item>
          <Descriptions.Item label={t('sectionType.modifierAccount')}>
            {sectionType.modifierAccount}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.modifyTime')}>
            {formatTimestamp(sectionType.gmtModified)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('sectionType.image')}>
        <div className="fixture-image-container">
          <Image
            src={sectionType.imageUrl}
            alt={``}
            width={300}
            height={225}
            className="fixture-image"
            style={{ 
              width: '100%',
              borderRadius: 6,
              border: '1px solid #d9d9d9'
            }}
          />
        </div>
      </Card>

      <Card>
        <Table
            dataSource={sectionDetails}
            rowKey={(record, index) => index?.toString() || "0"}
            pagination={false}
            size="small"
            scroll={{ y: 500 }}
            columns={[
              {
                title: t("sectionType.layer"),
                dataIndex: "layerIndex",
                key: "layerIndex",
                width: 80,
              },
              {
                title: t("sectionType.fixtureType"),
                key: "fixtureType",
                width: 100,
                render: (record: SectionDetail) => {
                  // console.log("****", record);

                  return (
                    <div>
                      <div>
                        {record.fixtureTypeName ||
                          t("sectionType.notSelected")}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {record.fixtureTypeCode || ""}
                      </div>
                    </div>
                  );
                },
              },
              {
                title: t("sectionType.quantity"),
                dataIndex: "quantity",
                key: "quantity",
                width: 100,
                render: (value: number) => value || 1,
              },
              {
                title: t("sectionType.holePosition"),
                dataIndex: "holePosition",
                key: "holePosition",
                width: 100,
              }
            ]}
          />
      </Card>
    </div>
  );
};

export default SectionTypeDetail;